import type { GridCoordinate, MapDefinition, TerrainType } from "../domain/types";

const make = (
  id: string,
  name: string,
  description: string,
  width: number,
  height: number,
  entrances: GridCoordinate[],
  exits: GridCoordinate[],
  obstacles: GridCoordinate[][],
  terrainType: TerrainType = "grass",
  startingCurrency = 520,
  startingLives = 30,
): MapDefinition => {
  const terrain: Record<string, TerrainType> = {};
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) terrain[`${x},${y}`] = terrainType;
  return Object.freeze({
    id,
    displayName: name,
    description,
    width,
    height,
    tileSize: 48,
    backgroundAsset: `map-${id}`,
    entrances: entrances.map((cell, i) => ({
      id: `entrance-${i + 1}`,
      cell,
      label: `Entrance ${i + 1}`,
    })),
    exits: exits.map((cell, i) => ({ id: `exit-${i + 1}`, cell, label: `Exit ${i + 1}` })),
    obstacles: obstacles.map((cells, i) => ({ id: `obstacle-${i + 1}`, cells })),
    protectedCells: [],
    terrain,
    movementCosts: { grass: 1, dirt: 0.9, cobble: 0.8, water: 4, stone: 1.5 },
    startingCurrency,
    startingLives,
  });
};
export const CASTLE_APPROACH = make(
  "castle-approach",
  "Castle Approach",
  "Open meadow before Lion Gate.",
  24,
  16,
  [{ x: 0, y: 8 }],
  [{ x: 23, y: 8 }],
  [
    [
      { x: 5, y: 3 },
      { x: 6, y: 3 },
      { x: 5, y: 4 },
    ],
    [
      { x: 17, y: 11 },
      { x: 18, y: 11 },
      { x: 18, y: 12 },
    ],
    [{ x: 11, y: 7 }],
  ],
);
export const ROYAL_COURTYARD = make(
  "royal-courtyard",
  "Royal Courtyard",
  "Twin approaches around a central fountain.",
  22,
  16,
  [
    { x: 0, y: 4 },
    { x: 0, y: 12 },
  ],
  [{ x: 21, y: 8 }],
  [
    [
      { x: 10, y: 7 },
      { x: 11, y: 7 },
      { x: 10, y: 8 },
      { x: 11, y: 8 },
    ],
  ],
  "cobble",
  560,
  28,
);
export const SIEGE_GROUNDS = make(
  "siege-grounds",
  "Siege Grounds",
  "Mud, trenches, and dense companies.",
  26,
  16,
  [
    { x: 0, y: 3 },
    { x: 0, y: 12 },
  ],
  [{ x: 25, y: 8 }],
  [
    [
      { x: 8, y: 5 },
      { x: 8, y: 6 },
    ],
    [
      { x: 16, y: 10 },
      { x: 17, y: 10 },
    ],
  ],
  "dirt",
  600,
  27,
);
export const ENCHANTED_FOREST = make(
  "enchanted-forest",
  "Enchanted Forest",
  "Irregular clearings and ancient ruins.",
  23,
  17,
  [{ x: 0, y: 8 }],
  [{ x: 22, y: 8 }],
  [
    [
      { x: 6, y: 4 },
      { x: 7, y: 4 },
      { x: 6, y: 5 },
    ],
    [
      { x: 15, y: 11 },
      { x: 16, y: 11 },
      { x: 16, y: 12 },
    ],
  ],
  "grass",
  580,
  26,
);
export const FROZEN_KEEP = make(
  "frozen-keep",
  "Frozen Keep",
  "Narrow icy approaches beneath blue braziers.",
  22,
  17,
  [
    { x: 0, y: 5 },
    { x: 0, y: 12 },
  ],
  [{ x: 21, y: 8 }],
  [
    [
      { x: 7, y: 7 },
      { x: 8, y: 7 },
      { x: 9, y: 7 },
    ],
    [
      { x: 13, y: 10 },
      { x: 14, y: 10 },
    ],
  ],
  "stone",
  620,
  25,
);
export const COASTAL_CITADEL = make(
  "coastal-citadel",
  "Coastal Citadel",
  "Cliff bridges split the route to two gates.",
  25,
  16,
  [{ x: 0, y: 4 }],
  [
    { x: 24, y: 4 },
    { x: 24, y: 12 },
  ],
  [
    [
      { x: 11, y: 0 },
      { x: 11, y: 1 },
      { x: 11, y: 14 },
      { x: 11, y: 15 },
    ],
  ],
  "cobble",
  650,
  24,
);
export const UNDERGROUND_STRONGHOLD = make(
  "underground-stronghold",
  "Underground Stronghold",
  "Restricted build pockets among crystal caverns.",
  24,
  17,
  [
    { x: 0, y: 3 },
    { x: 0, y: 13 },
  ],
  [{ x: 23, y: 8 }],
  [
    [
      { x: 6, y: 6 },
      { x: 7, y: 6 },
      { x: 8, y: 6 },
    ],
    [
      { x: 15, y: 10 },
      { x: 16, y: 10 },
      { x: 17, y: 10 },
    ],
  ],
  "stone",
  680,
  23,
);
export const VOLCANIC_FORTRESS = make(
  "volcanic-fortress",
  "Volcanic Fortress",
  "Multiple fiery approaches to the final citadel.",
  26,
  18,
  [
    { x: 0, y: 3 },
    { x: 0, y: 9 },
    { x: 0, y: 15 },
  ],
  [
    { x: 25, y: 6 },
    { x: 25, y: 12 },
  ],
  [
    [
      { x: 9, y: 7 },
      { x: 9, y: 8 },
      { x: 9, y: 9 },
    ],
    [
      { x: 17, y: 8 },
      { x: 17, y: 9 },
      { x: 17, y: 10 },
    ],
  ],
  "stone",
  750,
  22,
);
export const MAPS: Readonly<Record<string, MapDefinition>> = Object.freeze(
  Object.fromEntries(
    [
      CASTLE_APPROACH,
      ROYAL_COURTYARD,
      SIEGE_GROUNDS,
      ENCHANTED_FOREST,
      FROZEN_KEEP,
      COASTAL_CITADEL,
      UNDERGROUND_STRONGHOLD,
      VOLCANIC_FORTRESS,
    ].map((m) => [m.id, m]),
  ),
);
